// Centralised API client with optional Authorization Bearer from localStorage.
import axios from "axios";
import { getGuestId } from "./guestId";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const SESSION_KEY = "meyecam.session_token";

const client = axios.create({ baseURL: API });

// Extract album slug from any URL like /albums/<slug>/... or /album/<slug>/... or /upload/<slug>/...
// Also handles /albums/<slug>/media, /albums/<slug>/subfolders, etc.
const _slugFromUrl = (url) => {
  const m = String(url || "").match(/\/(?:albums?|upload)\/([^/?#]+)/);
  return m ? m[1] : null;
};

export const ALBUM_PASS_PREFIX = "meyecam.pass.";
export function getAlbumPassToken(slug) {
  try { return localStorage.getItem(ALBUM_PASS_PREFIX + slug) || null; } catch { return null; }
}
export function setAlbumPassToken(slug, token) {
  try {
    if (token) localStorage.setItem(ALBUM_PASS_PREFIX + slug, token);
    else localStorage.removeItem(ALBUM_PASS_PREFIX + slug);
  } catch { /* storage full */ }
}

// Host-only cache of the plaintext passcode so we can include it in Share text.
// Never sent to the server; only the host's own device reads it.
const HOST_PLAIN_PREFIX = "meyecam.host_pass.";
export function getHostPasscodePlain(slug) {
  try { return localStorage.getItem(HOST_PLAIN_PREFIX + slug) || null; } catch { return null; }
}
export function setHostPasscodePlain(slug, plain) {
  try {
    if (plain) localStorage.setItem(HOST_PLAIN_PREFIX + slug, plain);
    else localStorage.removeItem(HOST_PLAIN_PREFIX + slug);
  } catch { /* storage full */ }
}

client.interceptors.request.use((config) => {
  const token = localStorage.getItem(SESSION_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  try {
    const gid = getGuestId();
    if (gid) config.headers["X-Guest-Id"] = gid;
  } catch (_) {
    // ignore — guest id is best-effort
  }
  // Auto-attach the album passcode token if we have one cached for this URL.
  const slug = _slugFromUrl(config.url);
  if (slug) {
    const pt = getAlbumPassToken(slug);
    if (pt) config.headers["X-Album-Passcode"] = pt;
  }
  return config;
});

export default client;

// Convenience helpers
export const Albums = {
  list: () => client.get("/albums").then((r) => r.data),
  get: (slug) => client.get(`/albums/${slug}`).then((r) => r.data),
  create: (body) => client.post("/albums", body).then((r) => r.data),
  patch: (slug, body) => client.patch(`/albums/${slug}`, body).then((r) => r.data),
  remove: (slug) => client.delete(`/albums/${slug}`).then((r) => r.data),
  verifyPasscode: (slug, passcode) =>
    client.post(`/albums/${slug}/verify-passcode`, { passcode }).then((r) => r.data),
};

export const Subfolders = {
  list: (slug) => client.get(`/albums/${slug}/subfolders`).then((r) => r.data),
  listMine: (slug, guestId) =>
    client.get(`/albums/${slug}/subfolders`, { params: { guest_id: guestId, mine_only: true } }).then((r) => r.data),
  create: (slug, name, guestId) =>
    client.post(`/albums/${slug}/subfolders`, { name, guest_id: guestId }).then((r) => r.data),
  patch: (id, body) => client.patch(`/subfolders/${id}`, body).then((r) => r.data),
  reorder: (slug, ids) =>
    client.post(`/albums/${slug}/subfolders/reorder`, { ids }).then((r) => r.data),
  remove: (id) => client.delete(`/subfolders/${id}`).then((r) => r.data),
};

export const Media = {
  list: (slug) => client.get(`/albums/${slug}/media`).then((r) => r.data),
  thumbUrl: (id) => `${API}/media/${id}/thumb`,
  previewUrl: (id) => `${API}/media/${id}/preview`,
  downloadUrl: (id) => `${API}/media/${id}/download`,
  patch: (id, body) => client.patch(`/media/${id}`, body).then((r) => r.data),
  remove: (id) => client.delete(`/media/${id}`).then((r) => r.data),
  zipUrl: (slug) => `${API}/album/${slug}/zip`,
  deleteSelection: (slug, ids) =>
    client.post(`/album/${slug}/delete-selection`, { ids }).then((r) => r.data),
  moveSelection: (slug, ids, { subfolder_id = null, new_subfolder_name = null } = {}) =>
    client
      .post(`/album/${slug}/move-selection`, { ids, subfolder_id, new_subfolder_name })
      .then((r) => r.data),
};

export const Activity = {
  for: (slug, name, since, guestId) =>
    client.get(`/albums/${slug}/activity`, { params: { name, since, guest_id: guestId } }).then((r) => r.data),
};

export const Tips = {
  checkout: (body) => client.post("/tips/checkout-session", body).then((r) => r.data),
  status: (sid) => client.get(`/tips/checkout-status/${sid}`).then((r) => r.data),
};

export const Messages = {
  send: (slug, sender_name, text) =>
    client.post(`/albums/${slug}/messages`, { sender_name, text }).then((r) => r.data),
  list: (slug) => client.get(`/albums/${slug}/messages`).then((r) => r.data),
  unread: (slug) => client.get(`/albums/${slug}/messages/unread-count`).then((r) => r.data),
  markRead: (mid) => client.post(`/messages/${mid}/read`).then((r) => r.data),
  remove: (mid) => client.delete(`/messages/${mid}`).then((r) => r.data),
};

export const Comments = {
  list: (mid) => client.get(`/media/${mid}/comments`).then((r) => r.data),
  post: (mid, author_name, text) =>
    client.post(`/media/${mid}/comments`, { author_name, text }).then((r) => r.data),
  remove: (cid) => client.delete(`/comments/${cid}`).then((r) => r.data),
};

// Face registrations & per-guest virtual albums.
// The client owns face-detection (via face-api.js); the server just stores
// the resulting 128-dim descriptors and does the similarity search.
export const Faces = {
  register: (slug, { guest_id, name, descriptor, avatar }) =>
    client.post(`/albums/${slug}/faces/register`, { guest_id, name, descriptor, avatar }).then((r) => r.data),
  savePhoto: (slug, media_id, descriptors) =>
    client.post(`/albums/${slug}/faces/photo`, { media_id, descriptors }).then((r) => r.data),
  registrations: (slug) =>
    client.get(`/albums/${slug}/faces/registrations`).then((r) => r.data),
  indexedIds: (slug) =>
    client.get(`/albums/${slug}/faces/indexed-ids`).then((r) => r.data),
  matches: (slug, guestId) =>
    client.get(`/albums/${slug}/faces/matches`, { params: { guest_id: guestId } }).then((r) => r.data),
};

// Saved people-filter combos ("Me + Terry + Pavan"), private to their creator.
export const VirtualAlbums = {
  list: (slug, guestId) =>
    client.get(`/albums/${slug}/virtual-albums`, { params: { guest_id: guestId } }).then((r) => r.data),
  create: (slug, body) =>
    client.post(`/albums/${slug}/virtual-albums`, body).then((r) => r.data),
  remove: (slug, vid, guestId) =>
    client.delete(`/albums/${slug}/virtual-albums/${vid}`, { params: { guest_id: guestId } }).then((r) => r.data),
};

export const Auth = {
  exchange: (sessionId) => client.post("/auth/session", { session_token: sessionId }).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
};

export const Host = {
  stats: () => client.get("/host/stats").then((r) => r.data),
};

// Albums from other hosts that a signed-in user has visited.
export const Joined = {
  join: (slug) => client.post(`/albums/${slug}/join`).then((r) => r.data),
  leave: (slug) => client.delete(`/albums/${slug}/join`).then((r) => r.data),
  list: () => client.get("/host/joined-albums").then((r) => r.data),
};

export async function uploadFiles(slug, { uploaderName, subfolderId, title, description, guestId, files, onProgress }) {
  const form = new FormData();
  form.append("uploader_name", uploaderName);
  if (subfolderId) form.append("subfolder_id", subfolderId);
  if (title) form.append("title", title);
  if (description) form.append("description", description);
  if (guestId) form.append("guest_id", guestId);
  files.forEach((f) => form.append("files", f, f.name));
  const token = localStorage.getItem(SESSION_KEY);
  const passToken = getAlbumPassToken(slug);
  return axios.post(`${API}/upload/${slug}`, form, {
    headers: {
      "Content-Type": "multipart/form-data",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(passToken ? { "X-Album-Passcode": passToken } : {}),
    },
    timeout: 30 * 60 * 1000, // 30 min — large videos on slow mobile uplinks
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
    onUploadProgress: (e) => {
      if (e.total && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  }).then((r) => r.data);
}


// Chunked upload — used for files larger than the CDN's single-request cap.
// Slices the file client-side, sends each piece, then asks the backend to assemble + push to storage.
// Resumable: per-file upload_id is cached in localStorage; if the user reloads
// or the connection drops, we ask the server which chunks it already has and
// only re-send the missing ones.
const CHUNK_BYTES = 5 * 1024 * 1024;       // 5 MiB — well below any CDN cap
export const CHUNKED_THRESHOLD = 200 * 1024 * 1024; // > 200 MiB → use chunked path

const RESUME_PREFIX = "meyecam.upload.resume.";
function _fileKey(slug, file) {
  return `${RESUME_PREFIX}${slug}::${file.name}::${file.size}::${file.lastModified}`;
}
function _saveResume(slug, file, uploadId) {
  try { localStorage.setItem(_fileKey(slug, file), uploadId); } catch (_) {
    // localStorage may be unavailable in private mode — best-effort
  }
}
function _readResume(slug, file) {
  try { return localStorage.getItem(_fileKey(slug, file)); } catch (_) { return null; }
}
function _clearResume(slug, file) {
  try { localStorage.removeItem(_fileKey(slug, file)); } catch (_) {
    // ignore
  }
}

export async function uploadFileChunked(slug, file, { uploaderName, subfolderId, title, description, guestId, onProgress, onPhase, signal, onResume }) {
  const totalChunks = Math.ceil(file.size / CHUNK_BYTES);

  // 1. Resume? Look for a cached upload_id and confirm the server still has it.
  let uploadId = null;
  let alreadyReceived = new Set();
  const cached = _readResume(slug, file);
  if (cached) {
    try {
      const status = await axios.get(
        `${API}/upload/${slug}/chunked/${cached}/status`,
        { signal, timeout: 15000 }
      ).then((r) => r.data);
      if (status.total_chunks === totalChunks && status.size === file.size) {
        uploadId = cached;
        alreadyReceived = new Set(status.received || []);
        if (onResume) onResume({ have: alreadyReceived.size, of: totalChunks });
      } else {
        _clearResume(slug, file);
      }
    } catch (_) {
      // session expired or missing — start fresh
      _clearResume(slug, file);
    }
  }

  // 2. No resumable session — open a new one
  if (!uploadId) {
    const passToken = getAlbumPassToken(slug);
    const start = await axios.post(`${API}/upload/${slug}/chunked/start`, {
      uploader_name: uploaderName,
      guest_id: guestId || null,
      filename: file.name,
      content_type: file.type || "application/octet-stream",
      size: file.size,
      total_chunks: totalChunks,
      subfolder_id: subfolderId || null,
      title: title || null,
      description: description || null,
    }, {
      signal,
      headers: passToken ? { "X-Album-Passcode": passToken } : {},
    }).then((r) => r.data);
    uploadId = start.upload_id;
    _saveResume(slug, file, uploadId);
  }

  // 3. Upload missing chunks sequentially with retries
  try {
    for (let i = 0; i < totalChunks; i++) {
      if (alreadyReceived.has(i)) {
        if (onProgress) onProgress(Math.round(((i + 1) / totalChunks) * 100));
        continue;
      }
      const offset = i * CHUNK_BYTES;
      const blob = file.slice(offset, Math.min(offset + CHUNK_BYTES, file.size));
      const form = new FormData();
      form.append("upload_id", uploadId);
      form.append("chunk_index", String(i));
      form.append("chunk", blob, `chunk-${i}`);

      let lastErr = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          await axios.post(`${API}/upload/${slug}/chunked/chunk`, form, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 5 * 60 * 1000,
            signal,
          });
          lastErr = null;
          break;
        } catch (err) {
          lastErr = err;
          if (err?.name === "CanceledError" || err?.name === "AbortError") throw err;
          await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
        }
      }
      if (lastErr) throw lastErr;
      if (onProgress) onProgress(Math.round(((i + 1) / totalChunks) * 100));
    }

    // 4. Finalize — backend assembles + pushes to object storage
    if (onPhase) onPhase("finalizing");
    const finish = await axios.post(`${API}/upload/${slug}/chunked/finish`, {
      upload_id: uploadId,
    }, { signal, timeout: 10 * 60 * 1000 }).then((r) => r.data);

    if (onPhase) onPhase("done");
    _clearResume(slug, file);
    return finish;
  } catch (err) {
    // Leave the resume key in place so a retry can pick up where this left off.
    // Only on a deliberate abort do we tear down server-side staging.
    if (err?.name === "CanceledError" || err?.name === "AbortError") {
      axios.delete(`${API}/upload/${slug}/chunked/${uploadId}`).catch(() => {});
      _clearResume(slug, file);
    }
    throw err;
  }
}
